/**
 * Hook para gerenciar submissões do website Its So Clean
 *
 * Facilita o uso da API em componentes React com gerenciamento de estado,
 * loading, erros, filtros e paginação.
 */

import { useState, useEffect, useCallback } from 'react';
import itsSoCleanApi from '../services/itsSoCleanApi.js';

const useWebsiteSubmissions = () => {
  // Estado das estatísticas
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    newSubmissions: 0,
    repliedSubmissions: 0,
    totalUsers: 0,
  });

  // Estado das submissões (raw)
  const [rawSubmissions, setRawSubmissions] = useState([]);
  // Estado das conversas agrupadas por cliente
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 100, // Aumentar limite para ter mais submissões para agrupar
    offset: 0,
    hasMore: false,
  });

  // Estado de filtros
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });

  // Estado de loading e erros
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [error, setError] = useState(null);

  // Estado para conversa selecionada (cliente com todas suas submissões)
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedSubmissionReplies, setSelectedSubmissionReplies] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Estado para envio de resposta
  const [sendingReply, setSendingReply] = useState(false);

  /**
   * Agrupa submissões por cliente (email)
   */
  const groupSubmissionsByClient = (submissions) => {
    const grouped = {};

    submissions.forEach(sub => {
      const key = sub.user_email || sub.user_id || 'unknown';
      if (!grouped[key]) {
        grouped[key] = {
          clientEmail: sub.user_email,
          clientName: sub.user_name,
          clientPhone: sub.user_phone,
          clientAddress: sub.user_address,
          submissions: [],
          latestDate: sub.created_at,
          hasUnread: false,
          totalMessages: 0,
        };
      }

      grouped[key].submissions.push(sub);
      grouped[key].totalMessages++;

      // Atualizar data mais recente
      if (new Date(sub.created_at) > new Date(grouped[key].latestDate)) {
        grouped[key].latestDate = sub.created_at;
        grouped[key].clientName = sub.user_name || grouped[key].clientName;
        grouped[key].clientPhone = sub.user_phone || grouped[key].clientPhone;
        grouped[key].clientAddress = sub.user_address || grouped[key].clientAddress;
      }

      // Verificar se tem mensagem não lida
      if (sub.status === 'new') {
        grouped[key].hasUnread = true;
      }
    });

    // Converter para array e ordenar por data mais recente
    return Object.values(grouped)
      .map(conv => ({
        ...conv,
        // Ordenar submissões por data (mais antiga primeiro para exibir como conversa)
        submissions: conv.submissions.sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
        ),
      }))
      .sort((a, b) => new Date(b.latestDate) - new Date(a.latestDate));
  };

  /**
   * Carrega estatísticas do dashboard
   */
  const loadStats = useCallback(async () => {
    try {
      const data = await itsSoCleanApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
      setError('Falha ao carregar estatísticas');
    }
  }, []);

  /**
   * Carrega lista de submissões com filtros
   * Por padrão exclui arquivadas (a menos que filtro seja explicitamente "archived")
   */
  const loadSubmissions = useCallback(async (resetOffset = true) => {
    setLoadingSubmissions(true);
    setError(null);

    try {
      const params = {
        search: filters.search,
        limit: pagination.limit,
        offset: resetOffset ? 0 : pagination.offset,
      };

      // Se filtro de status específico, passar para API
      if (filters.status) {
        params.status = filters.status;
      }

      const data = await itsSoCleanApi.getSubmissions(params);

      // Filtrar arquivadas localmente se não houver filtro de status específico
      let filteredSubmissions = data.submissions || [];
      if (!filters.status) {
        // "Todas" = todas EXCETO arquivadas
        filteredSubmissions = filteredSubmissions.filter(s => s.status !== 'archived');
      }

      setRawSubmissions(filteredSubmissions);

      // Agrupar por cliente
      const grouped = groupSubmissionsByClient(filteredSubmissions);
      setConversations(grouped);

      setPagination(prev => ({
        ...prev,
        ...data.pagination,
        offset: resetOffset ? 0 : prev.offset,
      }));
    } catch (err) {
      console.error('Erro ao carregar submissões:', err);
      setError('Falha ao carregar submissões');
    } finally {
      setLoadingSubmissions(false);
    }
  }, [filters, pagination.limit, pagination.offset]);

  /**
   * Carrega dados iniciais (stats + submissões)
   */
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([loadStats(), loadSubmissions(true)]);
    } catch (err) {
      console.error('Erro ao carregar dados iniciais:', err);
    } finally {
      setLoading(false);
    }
  }, [loadStats, loadSubmissions]);

  /**
   * Seleciona uma conversa (cliente) e carrega detalhes de todas as submissões
   * Otimizado: usa Promise.all para carregar tudo em paralelo
   */
  const selectConversation = useCallback(async (conversation) => {
    setLoadingDetails(true);
    setSelectedConversation(conversation);

    try {
      // Carregar respostas de TODAS as submissões em PARALELO
      const repliesPromises = conversation.submissions.map(async (sub) => {
        try {
          const data = await itsSoCleanApi.getSubmissionById(sub.id);
          return { id: sub.id, replies: data.replies || [] };
        } catch (err) {
          console.error(`Erro ao carregar submissão ${sub.id}:`, err);
          return { id: sub.id, replies: [] };
        }
      });

      const repliesResults = await Promise.all(repliesPromises);
      const repliesMap = {};
      repliesResults.forEach(r => { repliesMap[r.id] = r.replies; });
      setSelectedSubmissionReplies(repliesMap);

      // Identificar submissões não lidas e marcar como lidas em PARALELO
      const unreadSubmissions = conversation.submissions.filter(s => s.status === 'new');
      if (unreadSubmissions.length > 0) {
        // Marcar todas como lidas em paralelo (não precisa esperar)
        Promise.all(
          unreadSubmissions.map(sub =>
            itsSoCleanApi.updateSubmissionStatus(sub.id, 'read').catch(err =>
              console.error(`Erro ao marcar ${sub.id} como lida:`, err)
            )
          )
        );

        const updatedIds = unreadSubmissions.map(s => s.id);

        // Atualizar status na lista local
        setRawSubmissions(prev =>
          prev.map(s => updatedIds.includes(s.id) ? { ...s, status: 'read' } : s)
        );

        // Reagrupar conversas
        setConversations(prev =>
          prev.map(conv => {
            if (conv.clientEmail === conversation.clientEmail) {
              return {
                ...conv,
                hasUnread: false,
                submissions: conv.submissions.map(s =>
                  updatedIds.includes(s.id) ? { ...s, status: 'read' } : s
                ),
              };
            }
            return conv;
          })
        );

        loadStats();
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      setError('Falha ao carregar detalhes da conversa');
    } finally {
      setLoadingDetails(false);
    }
  }, [loadStats]);

  /**
   * Fecha painel de detalhes
   */
  const closeDetails = useCallback(() => {
    setSelectedConversation(null);
    setSelectedSubmissionReplies({});
  }, []);

  /**
   * Envia resposta para a submissão mais recente de uma conversa
   */
  const sendReply = useCallback(async (submissionId, message) => {
    setSendingReply(true);

    try {
      await itsSoCleanApi.replyToSubmission(submissionId, message);

      // Atualizar status na lista local
      setRawSubmissions(prev =>
        prev.map(s => s.id === submissionId ? { ...s, status: 'replied' } : s)
      );

      // Atualizar respostas no mapa
      setSelectedSubmissionReplies(prev => ({
        ...prev,
        [submissionId]: [
          ...(prev[submissionId] || []),
          {
            id: Date.now().toString(),
            message,
            admin_name: 'Admin',
            created_at: new Date().toISOString(),
          },
        ],
      }));

      // Atualizar conversa selecionada
      if (selectedConversation) {
        setSelectedConversation(prev => ({
          ...prev,
          submissions: prev.submissions.map(s =>
            s.id === submissionId ? { ...s, status: 'replied' } : s
          ),
        }));
      }

      // Reagrupar conversas
      setConversations(prev =>
        prev.map(conv => ({
          ...conv,
          submissions: conv.submissions.map(s =>
            s.id === submissionId ? { ...s, status: 'replied' } : s
          ),
        }))
      );

      loadStats();
      return true;
    } catch (err) {
      console.error('Erro ao enviar resposta:', err);
      setError('Falha ao enviar resposta');
      return false;
    } finally {
      setSendingReply(false);
    }
  }, [selectedConversation, loadStats]);

  /**
   * Arquiva todas as submissões de uma conversa
   */
  const archiveConversation = useCallback(async (conversation) => {
    try {
      // Arquivar todas as submissões da conversa
      for (const sub of conversation.submissions) {
        await itsSoCleanApi.archiveSubmission(sub.id);
      }

      // Remover da lista local
      setRawSubmissions(prev =>
        prev.filter(s => !conversation.submissions.find(cs => cs.id === s.id))
      );

      // Remover conversa da lista
      setConversations(prev =>
        prev.filter(c => c.clientEmail !== conversation.clientEmail)
      );

      // Fechar detalhes se estava aberto
      if (selectedConversation?.clientEmail === conversation.clientEmail) {
        closeDetails();
      }

      loadStats();
      return true;
    } catch (err) {
      console.error('Erro ao arquivar:', err);
      setError('Falha ao arquivar conversa');
      return false;
    }
  }, [selectedConversation, closeDetails, loadStats]);

  /**
   * Marca conversa como não lida (marca a submissão mais recente)
   */
  const markConversationAsUnread = useCallback(async (conversation) => {
    try {
      // Marcar a submissão mais recente como não lida
      const latestSubmission = conversation.submissions[conversation.submissions.length - 1];
      await itsSoCleanApi.markAsUnread(latestSubmission.id);

      // Atualizar status na lista local
      setRawSubmissions(prev =>
        prev.map(s => s.id === latestSubmission.id ? { ...s, status: 'new' } : s)
      );

      // Atualizar conversas
      setConversations(prev =>
        prev.map(conv => {
          if (conv.clientEmail === conversation.clientEmail) {
            return {
              ...conv,
              hasUnread: true,
              submissions: conv.submissions.map(s =>
                s.id === latestSubmission.id ? { ...s, status: 'new' } : s
              ),
            };
          }
          return conv;
        })
      );

      loadStats();
      return true;
    } catch (err) {
      console.error('Erro ao marcar como não lida:', err);
      setError('Falha ao marcar como não lida');
      return false;
    }
  }, [loadStats]);

  /**
   * Atualiza filtros
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Limpa filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({ status: '', search: '' });
  }, []);

  /**
   * Vai para próxima página
   */
  const nextPage = useCallback(() => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit,
      }));
    }
  }, [pagination.hasMore]);

  /**
   * Vai para página anterior
   */
  const prevPage = useCallback(() => {
    if (pagination.offset > 0) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit),
      }));
    }
  }, [pagination.offset]);

  /**
   * Recarrega dados
   */
  const refresh = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Carregar dados iniciais ao montar
  useEffect(() => {
    loadInitialData();
  }, []);

  // Recarregar submissões quando filtros mudarem
  useEffect(() => {
    if (!loading) {
      loadSubmissions(true);
    }
  }, [filters]);

  // Recarregar submissões quando offset mudar (paginação)
  useEffect(() => {
    if (!loading && pagination.offset > 0) {
      loadSubmissions(false);
    }
  }, [pagination.offset]);

  return {
    // Estados
    stats,
    conversations,
    pagination,
    filters,
    loading,
    loadingSubmissions,
    loadingDetails,
    sendingReply,
    error,
    selectedConversation,
    selectedSubmissionReplies,

    // Ações
    loadStats,
    loadSubmissions,
    selectConversation,
    closeDetails,
    sendReply,
    archiveConversation,
    markConversationAsUnread,
    updateFilters,
    clearFilters,
    nextPage,
    prevPage,
    refresh,
  };
};

export default useWebsiteSubmissions;
